import {
  GESTOR_CENTRALIZADO_DEFAULT_PERIODO,
  GESTOR_CENTRALIZADO_DEFAULT_PER_PAGE,
  GESTOR_ESTADO_DESTINO_ACEPTADA,
} from "./gestor-centralizado.constants";

/**
 * GET /hp/service-system/v1/liquidaciones/centralizado
 * Query: idPeriodo?, estado?, idProv?, codigoTienda?, page, pageSize
 * Valor 0 en UI = Todos → no se envía el filtro.
 */

export function getDefaultGestorCentralizadoFilters() {
  const idPeriodo =
    Number.isFinite(GESTOR_CENTRALIZADO_DEFAULT_PERIODO) &&
    GESTOR_CENTRALIZADO_DEFAULT_PERIODO > 0
      ? GESTOR_CENTRALIZADO_DEFAULT_PERIODO
      : 0;

  return {
    idPeriodo,
    estado: 0,
    idProv: 0,
    codigoTienda: 0,
    estadoOs: 0,
    estadoLiquidacionOs: 0,
  };
}

export function normalizeGestorCentralizadoFilters(filters = {}) {
  const idPeriodo = Number(filters.idPeriodo ?? 0);
  const estado = Number(filters.estado ?? 0);
  const idProv = Number(filters.idProv ?? 0);
  const codigoTienda = Number(filters.codigoTienda ?? filters.idTienda ?? 0);
  const estadoOs = Number(filters.estadoOs ?? 0);
  const estadoLiquidacionOs = Number(filters.estadoLiquidacionOs ?? 0);
  const detSequenceId = String(filters.detSequenceId ?? "").trim();
  return {
    idPeriodo: Number.isFinite(idPeriodo) && idPeriodo > 0 ? idPeriodo : 0,
    estado: Number.isFinite(estado) && estado > 0 ? estado : 0,
    idProv: Number.isFinite(idProv) && idProv > 0 ? idProv : 0,
    codigoTienda:
      Number.isFinite(codigoTienda) && codigoTienda > 0 ? codigoTienda : 0,
    estadoOs: Number.isFinite(estadoOs) && estadoOs > 0 ? estadoOs : 0,
    estadoLiquidacionOs:
      Number.isFinite(estadoLiquidacionOs) && estadoLiquidacionOs > 0
        ? estadoLiquidacionOs
        : 0,
    detSequenceId,
  };
}

export function resolveEffectiveIdPeriodo(filters = {}) {
  const normalized = normalizeGestorCentralizadoFilters(filters);

  if (normalized.idPeriodo > 0) {
    return normalized.idPeriodo;
  }

  if (
    Number.isFinite(GESTOR_CENTRALIZADO_DEFAULT_PERIODO) &&
    GESTOR_CENTRALIZADO_DEFAULT_PERIODO > 0
  ) {
    return GESTOR_CENTRALIZADO_DEFAULT_PERIODO;
  }

  return 0;
}

export function areGestorCentralizadoFiltersEqual(left = {}, right = {}) {
  const a = normalizeGestorCentralizadoFilters(left);
  const b = normalizeGestorCentralizadoFilters(right);

  return (
    a.idPeriodo === b.idPeriodo &&
    a.estado === b.estado &&
    a.idProv === b.idProv &&
    a.codigoTienda === b.codigoTienda &&
    a.estadoOs === b.estadoOs &&
    a.estadoLiquidacionOs === b.estadoLiquidacionOs &&
    a.detSequenceId === b.detSequenceId
  );
}

export function buildGestorCentralizadoQueryParams(
  filters = {},
  pagination = {},
) {
  const normalized = normalizeGestorCentralizadoFilters(filters);
  const idPeriodo = resolveEffectiveIdPeriodo(filters);
  const page = Number(pagination.page ?? 1);
  const pageSize = Number(
    pagination.pageSize ??
      pagination.perPage ??
      GESTOR_CENTRALIZADO_DEFAULT_PER_PAGE,
  );

  const params = {
    page: Number.isFinite(page) && page > 0 ? page : 1,
    pageSize:
      Number.isFinite(pageSize) && pageSize > 0
        ? pageSize
        : GESTOR_CENTRALIZADO_DEFAULT_PER_PAGE,
  };

  if (idPeriodo > 0) {
    params.idPeriodo = idPeriodo;
  }

  if (normalized.estado > 0) {
    params.estado = normalized.estado;
  }

  if (normalized.idProv > 0) {
    params.idProv = normalized.idProv;
  }

  if (normalized.codigoTienda > 0) {
    params.codigoTienda = normalized.codigoTienda;
  }
  if (normalized.estadoOs > 0) {
    params.estadoOs = normalized.estadoOs;
  }

  if (normalized.estadoLiquidacionOs > 0) {
    params.estadoLiquidacionOs = normalized.estadoLiquidacionOs;
  }
  if (normalized.detSequenceId) {
    params.detSequenceId = normalized.detSequenceId;
  }
  return params;
}

export function parseGestorCentralizadoTotal(response, fallback = 0) {
  const candidates = [
    response?.totalRegistros,
    response?.total,
    response?.totalRecords,
    response?.totalRows,
    response?.count,
    response?.data?.totalRegistros,
    response?.data?.total,
    response?.paginacion?.totalRegistros,
    response?.pagination?.total,
  ];

  for (const value of candidates) {
    const total = Number(value);
    if (Number.isFinite(total) && total >= 0) {
      return total;
    }
  }

  return fallback;
}

export function resolveGestorCentralizadoPagedTotal(
  response,
  items = [],
  pagination = {},
) {
  const explicitTotal = parseGestorCentralizadoTotal(response, -1);
  if (explicitTotal >= 0) {
    return explicitTotal;
  }

  const page = Number(pagination.page ?? 1);
  const pageSize = Number(
    pagination.pageSize ??
      pagination.perPage ??
      GESTOR_CENTRALIZADO_DEFAULT_PER_PAGE,
  );
  const safePage = Number.isFinite(page) && page > 0 ? page : 1;
  const safePageSize =
    Number.isFinite(pageSize) && pageSize > 0
      ? pageSize
      : GESTOR_CENTRALIZADO_DEFAULT_PER_PAGE;

  if (items.length < safePageSize) {
    return (safePage - 1) * safePageSize + items.length;
  }

  return safePage * safePageSize + 1;
}

export function parseGestorCentralizadoErrorMessage(
  error,
  fallback = "Ocurrió un error al cargar los datos.",
) {
  if (error?.message?.startsWith("Falta la variable de entorno")) {
    return error.message;
  }

  if (error?.code === "ECONNABORTED") {
    return "No se pudo cargar la información. Verifique su conexión e intente nuevamente.";
  }

  const { data, status } = error?.response ?? {};

  if (typeof data === "string" && data.trim()) {
    return data.trim();
  }

  if (data?.message) {
    return data.message;
  }

  if (data?.success === false && data?.message) {
    return data.message;
  }

  if (status === 400) {
    return fallback;
  }

  return fallback;
}

export function resolveEstadoLiquidacion(response, items = []) {
  const fromResponse = response?.estado ?? response?.estadoLiquidacion;
  if (fromResponse) return String(fromResponse);

  const fromItem = items.find((row) => row.estadoLiquidacion)
    ?.estadoLiquidacion;
  if (fromItem) return String(fromItem);

  return "PRE_LIQUIDACION";
}

export function resolveIdLiquidacionCab(items = [], response = {}) {
  const fromResponse =
    response?.idLiquidacionCab ??
    response?.idLiquidacion ??
    response?.idLiquidacionCabecera;

  if (fromResponse) return Number(fromResponse);

  const fromItem = items.find((row) => Number(row.idLiquidacionCab) > 0);
  return fromItem ? Number(fromItem.idLiquidacionCab) : null;
}

export function getDirtyGestorCentralizadoRows(items = []) {
  const padres = items.filter((row) => !row.esAdicional);

  return padres.filter((padre) => {
    if (padre._dirty) return true;

    const tieneAdicionalDirty = items.some(
      (row) =>
        row.esAdicional &&
        row._parentDetSequenceId === padre.detSequenceId &&
        (row._dirty || row._nuevo),
    );

    const tieneAdicionalesEliminar =
      Array.isArray(padre._adicionalesEliminar) &&
      padre._adicionalesEliminar.length > 0;

    return tieneAdicionalDirty || tieneAdicionalesEliminar;
  });
}

export function buildGuardarGestorCentralizadoItem(row = {}, allItems = []) {
  const penalidad =
    row.penalidades && row.penalidades.length ? row.penalidades[0] : null;
  const eliminarPenalidad = !!row._eliminarPenalidad;

  const adicionalesDelPadre = allItems.filter(
    (item) =>
      item.esAdicional && item._parentDetSequenceId === row.detSequenceId,
  );

  return {
    idLiquidacionOs: row.idLiquidacionOs,
    estado: toEnumEstado(row.estado),  
    obsBackoffice: row.estado === "OBSERVADA" ? row._obsTmp : null,
    costoSinIgv: row.costoSinIgv ?? row.costoTotalSinIgv,
    cantidad: Number(row.cantidad),  
    eliminarPenalidad,
    penalidad:
      !eliminarPenalidad && penalidad && penalidad.tipoId
        ? {
            id: penalidad.idPenalidad ?? penalidad.id ?? null,
            tipoId: penalidad.tipoId,
            monto: Number(penalidad.monto || 0),
            exonerada: false,
            extras: {
              precioMercaderia: Number(penalidad.precioMercaderia || 0),
              flete: Number(penalidad.flete || 0),
            },
          }
        : null,
    adicionales: adicionalesDelPadre
      .filter((detalle) => detalle._dirty || detalle._nuevo)
      .map((detalle) => ({
        idDetalle: detalle._nuevo ? 0 : (detalle.idDetalle ?? 0),
        cantidad: Number(detalle.cantidad),
        costoSinIgv: detalle._costoUnitarioSinIgv ?? detalle.costoSinIgv,
        skuServicio: detalle.skuServicio || null,
        skuProducto: detalle.skuProducto || null,
        obsBackoffice: detalle.obsGestor || detalle.obsBackoffice || null,
        obsProveedor: detalle.obsProveedor || null,
        eliminar: false,
      })),
    adicionalesEliminar: Array.isArray(row._adicionalesEliminar)
      ? row._adicionalesEliminar
      : [],
  };
}
function toEnumEstado(estado) {
  const map = {
    BORRADOR: "Borrador",
    PENDIENTE_REVISION: "PendienteRevision",
    OBSERVADA: "Observada",
    ACEPTADA: "Aceptada",
    RESUELTA: "Resuelta",
    LIQUIDADA: "Liquidada",
  };
  return map[estado] || estado;
}
export function buildGuardarGestorCentralizadoPayload(
  items = [],
  idLiquidacionCab,
  usuario = "",
) {
  const dirtyRows = getDirtyGestorCentralizadoRows(items);

  return {
    idLiquidacionCab: Number(idLiquidacionCab),
    items: dirtyRows.map((row) =>
      buildGuardarGestorCentralizadoItem(row, items),
    ),
    usuario,
  };
}

export function buildObservacionGestorCentralizadoPayload(
  row = {},
  usuario = "",
  estadoDestino = GESTOR_ESTADO_DESTINO_ACEPTADA,
) {
  return {
    detSequenceId: String(row.detSequenceId ?? row.nroOs ?? "").trim(),
    obsBackoffice: String(
      row._obsTmp ?? row.obsBackoffice ?? row.obsGestor ?? "",
    ).trim(),
    estadoDestino: Number(estadoDestino),
    usuario,
  };
}

export function canConfirmObservacionGestor(row = {}) {
  if (row.estado === "PENDIENTE_REVISION") return true;

  if (!row.obsProveedor || !String(row.obsProveedor).trim()) return true;
  return Boolean(String(row._obsTmp ?? "").trim());
}
